import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';

// Derive WS URL from current origin so it works in both dev and production
const WS_URL = import.meta.env.VITE_WS_URL ||
  `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`;

// ═══════════════════════════════════════════════════════════════════════
//  Singleton WebSocket manager — one connection shared across all hooks
// ═══════════════════════════════════════════════════════════════════════

const MAX_RECONNECT = 10;
const BASE_DELAY = 1000;

let _ws = null;
let _connected = false;
let _reconnectAttempts = 0;
let _reconnectTimer = null;
let _refCount = 0; // number of mounted hook instances

const _subscribers = new Map(); // eventType -> Set<callback>
const _stateListeners = new Set(); // for useSyncExternalStore

function notifyStateListeners() {
  _stateListeners.forEach((fn) => fn());
}

function handleMessage(event) {
  try {
    const message = JSON.parse(event.data);

    // Notify typed subscribers
    if (message.type && _subscribers.has(message.type)) {
      _subscribers.get(message.type).forEach((cb) => {
        try { cb(message.data); } catch (e) { console.error('[WebSocket] Subscriber error:', e); }
      });
    }

    // Notify wildcard subscribers
    if (_subscribers.has('*')) {
      _subscribers.get('*').forEach((cb) => {
        try { cb(message); } catch (e) { console.error('[WebSocket] Wildcard subscriber error:', e); }
      });
    }
  } catch (e) {
    console.error('[WebSocket] Failed to parse message:', e);
  }
}

function wsConnect() {
  if (_ws?.readyState === WebSocket.OPEN || _ws?.readyState === WebSocket.CONNECTING) return;

  try {
    _ws = new WebSocket(WS_URL);

    _ws.onopen = () => {
      console.log('[WebSocket] Connected');
      _connected = true;
      _reconnectAttempts = 0;
      notifyStateListeners();
    };

    _ws.onclose = (event) => {
      console.log('[WebSocket] Disconnected', event.code, event.reason);
      _connected = false;
      _ws = null;
      notifyStateListeners();

      // Reconnect if not a clean close and there are still mounted consumers
      if (event.code !== 1000 && _refCount > 0 && _reconnectAttempts < MAX_RECONNECT) {
        const delay = Math.min(BASE_DELAY * Math.pow(2, _reconnectAttempts), 30000);
        console.log(`[WebSocket] Reconnecting in ${delay}ms (attempt ${_reconnectAttempts + 1}/${MAX_RECONNECT})`);
        _reconnectTimer = setTimeout(() => {
          _reconnectAttempts++;
          wsConnect();
        }, delay);
      }
    };

    _ws.onerror = (error) => {
      console.error('[WebSocket] Error:', error);
    };

    _ws.onmessage = handleMessage;
  } catch (e) {
    console.error('[WebSocket] Connection error:', e);
    _connected = false;
    notifyStateListeners();
  }
}

function wsDisconnect() {
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  if (_ws) { _ws.close(1000, 'Client disconnect'); _ws = null; }
  _connected = false;
  _reconnectAttempts = 0;
  notifyStateListeners();
}

function addRef() {
  _refCount++;
  if (_refCount === 1) wsConnect(); // first consumer → open connection
}

function removeRef() {
  _refCount = Math.max(0, _refCount - 1);
  if (_refCount === 0) wsDisconnect(); // last consumer gone → close
}

// ═══════════════════════════════════════════════════════════════════════
//  React hook — thin wrapper over the singleton
// ═══════════════════════════════════════════════════════════════════════

/**
 * WebSocket hook for real-time updates from the backend.
 * All instances share a single WebSocket connection.
 *
 * Usage:
 *   const { connected, subscribe, unsubscribe, send } = useWebSocket();
 */
function useWebSocket() {
  // Track connected state reactively via useSyncExternalStore
  const connected = useSyncExternalStore(
    (cb) => { _stateListeners.add(cb); return () => _stateListeners.delete(cb); },
    () => _connected,
  );

  // Ref-count: connect on first mount, disconnect when all unmount
  useEffect(() => {
    addRef();
    return () => removeRef();
  }, []);

  const subscribe = useCallback((eventType, callback) => {
    if (!_subscribers.has(eventType)) _subscribers.set(eventType, new Set());
    _subscribers.get(eventType).add(callback);
  }, []);

  const unsubscribe = useCallback((eventType, callback) => {
    if (_subscribers.has(eventType)) {
      _subscribers.get(eventType).delete(callback);
      if (_subscribers.get(eventType).size === 0) _subscribers.delete(eventType);
    }
  }, []);

  const send = useCallback((type, data) => {
    if (_ws?.readyState === WebSocket.OPEN) {
      _ws.send(JSON.stringify({ type, data }));
    }
  }, []);

  return { connected, subscribe, unsubscribe, send, reconnect: wsConnect, disconnect: wsDisconnect };
}

export default useWebSocket;
